import React, { useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Calculator as CalcIcon, ArrowRight, RotateCcw, Download, CalendarDays, Lock, X } from 'lucide-react';
import html2canvas from 'html2canvas';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';

// Research reconstitution / concentration calculator.
// EDITABLE: adjust the formula in `results` below if your original calculator differs.
const DEFAULTS = { productName: '', peptideMg: '5', waterMl: '2', targetMg: '0.25', targetUnit: 'mg' };
const NASAL_DEFAULTS = { productName: '', peptideMg: '5', doseMcg: '100', doseUnit: 'mcg', sprayMl: '0.10', sprays: '1' };
const VIAL_PRESETS = ['2', '5', '10', '15', '20', '30'];
const WATER_PRESETS = ['1', '2', '3'];
const SPRAY_PRESETS = ['0.05', '0.10', '0.14'];

export default function Calculator() {
  const [params] = useSearchParams();
  const initial = {
    productName: params.get('name') || DEFAULTS.productName,
    peptideMg: params.get('peptideMg') || DEFAULTS.peptideMg,
    waterMl: params.get('waterMl') || DEFAULTS.waterMl,
    targetMg: params.get('targetMg') || DEFAULTS.targetMg,
    targetUnit: DEFAULTS.targetUnit,
  };
  const [v, setV] = useState(initial);
  const [mode, setMode] = useState(params.get('mode') === 'nasal' ? 'nasal' : 'inject');
  const [n, setN] = useState({
    ...NASAL_DEFAULTS,
    productName: params.get('mode') === 'nasal' ? (params.get('name') || '') : NASAL_DEFAULTS.productName,
    peptideMg: params.get('mode') === 'nasal' && params.get('peptideMg') ? params.get('peptideMg') : NASAL_DEFAULTS.peptideMg,
  });
  const setNF = (k) => (e) => setN((s) => ({ ...s, [k]: e.target.value }));
  const [saving, setSaving] = useState(false);
  const [showLibPrompt, setShowLibPrompt] = useState(false);
  const resultsRef = useRef(null);
  const { toast } = useToast();
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));
  const reset = () => setV(DEFAULTS);

  // VIP library members can push the computed dosage straight into their Weekly Dosage Diary.
  const addToDiary = () => {
    const token = localStorage.getItem('alp_library_token');
    if (!token) { setShowLibPrompt(true); return; }
    if (!results || results.drawUnits == null) return;
    try {
      const key = 'alp_dosage_diary';
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      const entry = {
        id: `${Date.now()}`,
        itemId: '',
        name: (v.productName || 'Custom peptide').trim(),
        dose: `${v.targetMg}${v.targetUnit}`,
        time: 'Morning',
        notes: 'Added from calculator',
        days: [],
        units: results.drawUnits,
        vialMg: parseFloat(v.peptideMg),
        waterMl: parseFloat(v.waterMl),
      };
      localStorage.setItem(key, JSON.stringify([...existing, entry]));
      toast({ title: 'Added to your diary', description: 'Open the Client Library to view your Weekly Dosage Diary.' });
    } catch (e) {
      toast({ title: 'Could not add to diary', description: 'Please try again.', variant: 'destructive' });
    }
  };

  const saveImage = async () => {
    if (!resultsRef.current) return;
    setSaving(true);
    try {
      const canvas = await html2canvas(resultsRef.current, { backgroundColor: '#0A0A0A', scale: 2, useCORS: true, logging: false });
      const name = (v.productName || 'reconstitution').trim().replace(/[^a-z0-9]+/gi, '-').toLowerCase();
      const filename = `alp-${name}-dosage.png`;
      const done = await new Promise((resolve) => {
        if (canvas.toBlob) {
          canvas.toBlob((blob) => {
            if (!blob) { resolve(false); return; }
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(url), 1500);
            resolve(true);
          }, 'image/png');
        } else {
          const link = document.createElement('a');
          link.href = canvas.toDataURL('image/png');
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          resolve(true);
        }
      });
      if (done) toast({ title: 'Image saved', description: 'Check your downloads folder.' });
      else toast({ title: 'Could not save image', description: 'Please try again.', variant: 'destructive' });
    } catch (e) {
      toast({ title: 'Could not save image', description: 'Please try again.', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const results = useMemo(() => {
    const peptideMg = parseFloat(v.peptideMg);
    const waterMl = parseFloat(v.waterMl);
    const targetInput = parseFloat(v.targetMg);
    const targetMg = v.targetUnit === 'mcg' ? targetInput / 1000 : targetInput;
    if (!peptideMg || !waterMl || waterMl <= 0) return null;

    const concMcgPerMl = (peptideMg * 1000) / waterMl; // mcg per mL
    const concMgPerMl = peptideMg / waterMl;
    let drawMl = null;
    let drawUnits = null;
    let totalDoses = null;
    if (targetMg && targetMg > 0) {
      drawMl = targetMg / concMgPerMl;
      drawUnits = drawMl * 100; // U-100 insulin syringe units
      totalDoses = peptideMg / targetMg;
    }
    return { concMcgPerMl, concMgPerMl, drawMl, drawUnits, totalDoses };
  }, [v]);

  const nasalResults = useMemo(() => {
    const peptideMg = parseFloat(n.peptideMg);
    const doseInput = parseFloat(n.doseMcg);
    const doseMcg = n.doseUnit === 'mg' ? doseInput * 1000 : doseInput;
    const sprayMl = parseFloat(n.sprayMl);
    const sprays = Math.max(1, Math.round(parseFloat(n.sprays) || 1));
    if (!peptideMg || peptideMg <= 0 || !doseMcg || doseMcg <= 0 || !sprayMl || sprayMl <= 0) return null;
    const totalMcg = peptideMg * 1000;
    const volPerDose = sprays * sprayMl; // mL delivered per administration
    const liquidMl = (totalMcg * volPerDose) / doseMcg; // liquid to add to the vial
    const concMcgPerMl = totalMcg / liquidMl;
    const dosePerSpray = doseMcg / sprays;
    const totalSprays = liquidMl / sprayMl;
    const totalDoses = totalMcg / doseMcg;
    return { liquidMl, concMcgPerMl, dosePerSpray, totalSprays, totalDoses, sprays, sprayMl };
  }, [n]);

  return (
    <div className="bg-alp-black min-h-screen">
      <div className="border-b border-alp-line">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-16 text-center">
          <p className="text-alp-gold text-xs tracking-[0.3em] uppercase mb-3">Research Tool</p>
          <h1 className="font-serif text-5xl md:text-6xl text-alp-cream heading-glow">Reconstitution Calculator</h1>
          <p className="text-alp-gray mt-4 max-w-xl mx-auto">
            A clean concentration calculator for research calculation purposes. Enter your vial amount and solvent volume below.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 md:px-8 py-14">
        {/* Mode toggle */}
        <div className="flex justify-center gap-2 mb-10" data-testid="calc-mode-toggle">
          {[['inject', 'Injection'], ['nasal', 'Nasal Spray']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setMode(k)} data-testid={`calc-mode-${k}`}
              className={`px-6 py-2.5 rounded-full text-xs tracking-[0.18em] uppercase font-semibold border transition-colors ${mode === k ? 'bg-alp-gold text-alp-black border-alp-gold' : 'border-alp-line text-alp-gray hover:border-alp-gold/60'}`}>
              {l}
            </button>
          ))}
        </div>
        {mode === 'nasal' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* NASAL INPUTS */}
          <div className="border border-alp-gold/30 rounded-lg p-7 bg-alp-card/50">
            <div className="flex items-center gap-3 mb-6">
              <CalcIcon size={22} className="text-alp-gold" />
              <h2 className="font-serif text-2xl text-alp-cream">Nasal Inputs</h2>
            </div>
            <div className="space-y-5">
              <CalcField label="Product name (optional)" value={n.productName} onChange={setNF('productName')} type="text" inputMode="text" placeholder="e.g. DSIP 5mg" testid="nasal-product-name" />
              <div>
                <CalcField label="Peptide amount in vial (mg)" value={n.peptideMg} onChange={setNF('peptideMg')} testid="nasal-peptide-mg" />
                <div className="flex flex-wrap gap-2 mt-2.5">
                  {VIAL_PRESETS.map((mg) => (
                    <button key={mg} type="button" onClick={() => setN((s) => ({ ...s, peptideMg: mg }))} data-testid={`nasal-preset-vial-${mg}`} className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.1em] border transition-colors ${n.peptideMg === mg ? 'bg-alp-gold text-alp-black border-alp-gold' : 'border-alp-line text-alp-gray hover:border-alp-gold/60'}`}>{mg}mg</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-end justify-between gap-3 mb-2">
                  <Label className="text-xs tracking-[0.15em] uppercase text-alp-gray block">Desired dose per administration</Label>
                  <div className="flex gap-1" data-testid="nasal-dose-unit-toggle">
                    {['mcg', 'mg'].map((u) => (
                      <button key={u} type="button" onClick={() => setN((s) => ({ ...s, doseUnit: u }))} data-testid={`nasal-dose-unit-${u}`}
                        className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.1em] border transition-colors ${n.doseUnit === u ? 'bg-alp-gold text-alp-black border-alp-gold' : 'border-alp-line text-alp-gray hover:border-alp-gold/60'}`}>
                        {u}
                      </button>
                    ))}
                  </div>
                </div>
                <Input type="number" inputMode="decimal" value={n.doseMcg} onChange={setNF('doseMcg')} data-testid="nasal-dose-mcg"
                  className="bg-alp-dark border-alp-gold/40 text-alp-cream focus-visible:ring-alp-gold text-lg h-12" />
              </div>
              <CalcField label="Sprays per administration" value={n.sprays} onChange={setNF('sprays')} testid="nasal-sprays" />
              <div>
                <CalcField label="Volume per spray (mL)" value={n.sprayMl} onChange={setNF('sprayMl')} testid="nasal-spray-ml" />
                <div className="flex flex-wrap gap-2 mt-2.5">
                  {SPRAY_PRESETS.map((ml) => (
                    <button key={ml} type="button" onClick={() => setN((s) => ({ ...s, sprayMl: ml }))} data-testid={`nasal-preset-spray-${ml}`} className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.1em] border transition-colors ${n.sprayMl === ml ? 'bg-alp-gold text-alp-black border-alp-gold' : 'border-alp-line text-alp-gray hover:border-alp-gold/60'}`}>{ml}mL</button>
                  ))}
                </div>
                <p className="text-alp-gray/70 text-[11px] mt-2">Most nasal pumps deliver 0.05&ndash;0.14 mL per spray &mdash; check your device.</p>
              </div>
              <button onClick={() => setN(NASAL_DEFAULTS)} className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-alp-gray hover:text-alp-gold transition-colors">
                <RotateCcw size={14} /> Reset
              </button>
            </div>
          </div>

          {/* NASAL RESULTS */}
          <div className="border border-alp-gold/30 rounded-lg p-7 bg-alp-card/50">
            <h2 className="font-serif text-2xl text-alp-cream mb-6">Results</h2>
            {!nasalResults ? (
              <p className="text-alp-gray">Enter a vial amount, dose and spray volume to calculate.</p>
            ) : (
              <div className="space-y-4" data-testid="nasal-results" ref={resultsRef}>
                {n.productName ? (
                  <div className="text-center pb-1">
                    <span className="text-xs tracking-[0.2em] uppercase text-alp-gray">Product</span>
                    <div className="font-serif text-xl text-alp-gold">{n.productName}</div>
                  </div>
                ) : null}
                <div className="rounded-lg border border-alp-gold/40 bg-alp-dark/60 p-5 text-center" data-testid="nasal-liquid-result">
                  <p className="text-xs tracking-[0.2em] uppercase text-alp-gold mb-1.5">Liquid to add to the vial</p>
                  <div className="font-serif text-4xl text-alp-gold">{nasalResults.liquidMl.toFixed(2)} mL</div>
                  <p className="text-alp-gray text-xs mt-1.5">Then {nasalResults.sprays > 1 ? `${nasalResults.sprays} sprays` : '1 spray'} ({nasalResults.sprayMl} mL each) delivers your {parseFloat(n.doseMcg)} {n.doseUnit} dose.</p>
                </div>
                <ResultRow label="Concentration" value={`${nasalResults.concMcgPerMl.toFixed(0)} mcg/mL`} />
                <ResultRow label="Dose per spray" value={`${nasalResults.dosePerSpray.toFixed(0)} mcg`} />
                <div className="gold-divider my-2" />
                <ResultRow label="Total sprays in the vial" value={`~${Math.floor(nasalResults.totalSprays)}`} highlight />
                <ResultRow label="Total doses in the vial" value={`~${Math.floor(nasalResults.totalDoses)}`} highlight />
                {nasalResults.liquidMl > 10 && (
                  <p className="text-amber-400 text-xs leading-relaxed" data-testid="nasal-warn-high">{nasalResults.liquidMl.toFixed(1)} mL is more than most spray bottles hold &mdash; raise the dose, use fewer sprays, or split across bottles.</p>
                )}
                {nasalResults.liquidMl < 1 && (
                  <p className="text-amber-400 text-xs leading-relaxed" data-testid="nasal-warn-low">Under 1 mL is hard to work with in a spray bottle &mdash; consider a smaller spray volume or lower-strength vial.</p>
                )}
              </div>
            )}
            {nasalResults && (
              <button onClick={saveImage} disabled={saving} data-testid="nasal-save-image-btn" className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-xs tracking-[0.18em] uppercase font-semibold border border-alp-gold/50 text-alp-gold hover:bg-alp-gold hover:text-alp-black transition-colors disabled:opacity-50">
                <Download size={15} /> {saving ? 'Saving\u2026' : 'Save as Image'}
              </button>
            )}
          </div>
        </div>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* INPUTS */}
          <div className="border border-alp-gold/30 rounded-lg p-7 bg-alp-card/50">
            <div className="flex items-center gap-3 mb-6">
              <CalcIcon size={22} className="text-alp-gold" />
              <h2 className="font-serif text-2xl text-alp-cream">Inputs</h2>
            </div>
            <div className="space-y-5">
              <CalcField label="Product name (optional)" value={v.productName} onChange={set('productName')} type="text" inputMode="text" placeholder="e.g. Retatrutide 5mg" testid="calc-product-name" />
              <div>
                <CalcField label="Peptide amount in vial (mg)" value={v.peptideMg} onChange={set('peptideMg')} />
                <div className="flex flex-wrap gap-2 mt-2.5" data-testid="calc-vial-presets">
                  {VIAL_PRESETS.map((mg) => (
                    <button key={mg} type="button" onClick={() => setV((s) => ({ ...s, peptideMg: mg }))} data-testid={`calc-preset-vial-${mg}`} className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.1em] border transition-colors ${v.peptideMg === mg ? 'bg-alp-gold text-alp-black border-alp-gold' : 'border-alp-line text-alp-gray hover:border-alp-gold/60'}`}>{mg}mg</button>
                  ))}
                </div>
              </div>
              <div>
                <CalcField label="Bacteriostatic water added (mL)" value={v.waterMl} onChange={set('waterMl')} />
                <div className="flex flex-wrap gap-2 mt-2.5" data-testid="calc-water-presets">
                  {WATER_PRESETS.map((ml) => (
                    <button key={ml} type="button" onClick={() => setV((s) => ({ ...s, waterMl: ml }))} data-testid={`calc-preset-water-${ml}`} className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.1em] border transition-colors ${v.waterMl === ml ? 'bg-alp-gold text-alp-black border-alp-gold' : 'border-alp-line text-alp-gray hover:border-alp-gold/60'}`}>{ml}mL</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-end justify-between gap-3 mb-2">
                  <Label className="text-xs tracking-[0.15em] uppercase text-alp-gray block">Target amount per draw</Label>
                  <div className="flex gap-1" data-testid="calc-target-unit-toggle">
                    {['mg', 'mcg'].map((u) => (
                      <button key={u} type="button" onClick={() => setV((s) => ({ ...s, targetUnit: u }))} data-testid={`calc-target-unit-${u}`}
                        className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.1em] border transition-colors ${v.targetUnit === u ? 'bg-alp-gold text-alp-black border-alp-gold' : 'border-alp-line text-alp-gray hover:border-alp-gold/60'}`}>
                        {u}
                      </button>
                    ))}
                  </div>
                </div>
                <Input type="number" inputMode="decimal" value={v.targetMg} onChange={set('targetMg')} data-testid="calc-target-amount"
                  className="bg-alp-dark border-alp-gold/40 text-alp-cream focus-visible:ring-alp-gold text-lg h-12" />
              </div>
              <button onClick={reset} className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-alp-gray hover:text-alp-gold transition-colors">
                <RotateCcw size={14} /> Reset
              </button>
            </div>
          </div>

          {/* RESULTS */}
          <div className="border border-alp-gold/30 rounded-lg p-7 bg-alp-card/50">
            <h2 className="font-serif text-2xl text-alp-cream mb-6">Results</h2>
            {!results ? (
              <p className="text-alp-gray">Enter a valid peptide amount and water volume to calculate.</p>
            ) : (
              <div className="space-y-4" data-testid="calc-results" ref={resultsRef}>
                {v.productName ? (
                  <div className="text-center pb-1">
                    <span className="text-xs tracking-[0.2em] uppercase text-alp-gray">Product</span>
                    <div className="font-serif text-xl text-alp-gold" data-testid="calc-result-name">{v.productName}</div>
                  </div>
                ) : null}
                <ResultRow label="Concentration" value={`${results.concMgPerMl.toFixed(2)} mg/mL`} />
                <ResultRow label="Concentration" value={`${results.concMcgPerMl.toFixed(0)} mcg/mL`} />
                <div className="gold-divider my-2" />
                {results.drawMl != null ? (
                  <>
                    <ResultRow label="Volume to draw" value={`${results.drawMl.toFixed(3)} mL`} highlight />
                    <ResultRow label="On a U-100 insulin syringe" value={`${results.drawUnits.toFixed(1)} units (IU)`} highlight />
                    <ResultRow label="Doses in the vial" value={`~${Math.floor(results.totalDoses)}`} highlight />
                    <Syringe units={results.drawUnits} productName={v.productName} vialMg={v.peptideMg} waterMl={v.waterMl} />
                  </>
                ) : (
                  <p className="text-alp-gray text-sm">Enter a target amount to calculate draw volume.</p>
                )}
              </div>
            )}
            {results && results.drawMl != null && (
              <>
                <button onClick={saveImage} disabled={saving} data-testid="calc-save-image-btn" className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-xs tracking-[0.18em] uppercase font-semibold border border-alp-gold/50 text-alp-gold hover:bg-alp-gold hover:text-alp-black transition-colors disabled:opacity-50">
                  <Download size={15} /> {saving ? 'Saving\u2026' : 'Save as Image'}
                </button>
                <button onClick={addToDiary} data-testid="calc-add-to-diary-btn" className="mt-3 w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-xs tracking-[0.18em] uppercase font-semibold bg-alp-gold text-alp-black hover:bg-alp-goldlight transition-colors">
                  <CalendarDays size={15} /> Add this dosage to your diary
                </button>
              </>
            )}
          </div>
        </div>
        )}

        {/* DISCLAIMER */}
        <div className="hairline rounded-lg px-6 py-5 mt-8 text-center">
          <p className="text-alp-gray text-sm leading-relaxed">
            For research calculation purposes only. Not for human consumption. No medical or dosing advice is provided.
          </p>
        </div>

        <div className="text-center mt-10">
          <Link to="/shop" className="inline-flex items-center gap-2 bg-alp-gold text-alp-black px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">
            Browse Catalog <ArrowRight size={15} />
          </Link>
        </div>
      </div>

      {/* Library-access prompt for non-members */}
      {showLibPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4" data-testid="calc-lib-prompt" onClick={() => setShowLibPrompt(false)}>
          <div className="relative w-full max-w-md bg-alp-card border border-alp-gold/40 rounded-xl p-8 text-center" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowLibPrompt(false)} data-testid="calc-lib-prompt-close" className="absolute right-3 top-3 w-9 h-9 rounded-full bg-alp-black/60 border border-alp-line flex items-center justify-center text-alp-gray hover:text-alp-gold hover:border-alp-gold transition-colors">
              <X size={18} />
            </button>
            <div className="w-14 h-14 rounded-full border border-alp-gold/40 flex items-center justify-center mx-auto mb-5">
              <Lock size={22} className="text-alp-gold" />
            </div>
            <p className="text-alp-gold text-xs tracking-[0.3em] uppercase mb-2">Members Only</p>
            <h3 className="font-serif text-2xl text-alp-cream mb-3">Unlock the Dosage Diary</h3>
            <p className="text-alp-gray text-sm mb-7 leading-relaxed">The Weekly Dosage Diary is part of the Client Help Library. Purchase a digital guide to receive your access details, then log in to save dosages straight from this calculator.</p>
            <Link to="/shop" data-testid="calc-lib-prompt-shop" className="block w-full bg-alp-gold text-alp-black py-3 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors mb-3">
              Shop Digital Guides
            </Link>
            <Link to="/library" data-testid="calc-lib-prompt-login" className="block w-full border border-alp-gold/50 text-alp-gold py-3 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-gold hover:text-alp-black transition-colors">
              I already have access
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function CalcField({ label, value, onChange, type = 'number', inputMode = 'decimal', placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs tracking-[0.15em] uppercase text-alp-gray mb-2 block">{label}</Label>
      <Input
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className="bg-alp-dark border-alp-gold/40 text-alp-cream focus-visible:ring-alp-gold text-lg h-12"
      />
    </div>
  );
}

// Visual U-100 insulin syringe showing exactly where to fill. Screenshot-friendly.
function Syringe({ units, productName, vialMg, waterMl }) {
  const capped = Math.min(Math.max(units, 0), 100);
  const x0 = 60, barrelW = 400; // 0 IU at x0, 100 IU at x0+barrelW
  const fillX = x0 + (capped / 100) * barrelW;
  const over = units > 100;
  const ticks = [];
  for (let i = 0; i <= 100; i += 5) {
    const x = x0 + (i / 100) * barrelW;
    const major = i % 10 === 0;
    ticks.push(
      <g key={i}>
        <line x1={x} y1={38} x2={x} y2={major ? 28 : 32} stroke="#9A9384" strokeWidth={major ? 1.4 : 0.8} />
        {major && <text x={x} y={20} fontSize="10" fill="#9A9384" textAnchor="middle">{i}</text>}
      </g>
    );
  }
  return (
    <div className="mt-4 rounded-lg border border-alp-gold/30 bg-alp-dark/60 p-4" data-testid="calc-syringe">
      <p className="text-xs tracking-[0.18em] uppercase text-alp-gold mb-2 text-center">Fill your syringe to here</p>
      <svg viewBox="0 0 520 110" className="w-full" role="img" aria-label={`Fill insulin syringe to ${units.toFixed(1)} units`}>
        {/* needle */}
        <line x1={8} y1={60} x2={40} y2={60} stroke="#9A9384" strokeWidth="2" />
        <rect x={40} y={52} width={20} height={16} rx={2} fill="#2A2620" stroke="#9A9384" strokeWidth="1" />
        {/* barrel */}
        <rect x={x0} y={44} width={barrelW} height={32} rx={4} fill="#15120E" stroke="#C9A227" strokeWidth="1.5" />
        {/* gold fill */}
        <rect x={x0} y={44} width={fillX - x0} height={32} rx={4} fill="#C9A227" fillOpacity="0.55" />
        {/* ticks + labels */}
        {ticks}
        {/* fill marker */}
        <line x1={fillX} y1={30} x2={fillX} y2={90} stroke="#C9A227" strokeWidth="2" strokeDasharray="4 3" />
        <polygon points={`${fillX - 5},30 ${fillX + 5},30 ${fillX},38`} fill="#C9A227" />
        <text x={Math.min(fillX, x0 + barrelW - 10)} y={104} fontSize="12" fill="#C9A227" textAnchor="middle" fontWeight="bold">{units.toFixed(1)} IU</text>
        {/* plunger */}
        <rect x={x0 + barrelW} y={50} width={36} height={20} rx={2} fill="#2A2620" stroke="#9A9384" strokeWidth="1" />
        <rect x={x0 + barrelW + 36} y={42} width={8} height={36} rx={2} fill="#9A9384" />
      </svg>
      {productName ? <p className="text-center text-alp-cream text-sm mt-1">{productName}</p> : null}
      {over && (
        <p className="text-center text-amber-400 text-xs mt-2">Draw exceeds 100 IU — more than one full syringe is required per dose.</p>
      )}
      {vialMg && waterMl ? (
        <p className="text-center text-alp-gold/90 text-xs mt-2" data-testid="syringe-bac-reminder">Mixed with {parseFloat(waterMl)} mL BAC water in a {parseFloat(vialMg)} mg vial</p>
      ) : null}
      <p className="text-center text-alp-gray/70 text-[11px] mt-2">Markings shown on a standard U-100 insulin syringe. Research use only.</p>
    </div>
  );
}

function ResultRow({ label, value, highlight }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-alp-gray text-sm">{label}</span>
      <span className={`font-semibold ${highlight ? 'text-alp-gold text-xl' : 'text-alp-cream text-lg'}`}>{value}</span>
    </div>
  );
}
